import { Modal, Row, Col, Space, Divider, Alert } from 'antd';
import { TeamOutlined, UserOutlined, CheckOutlined, CloseOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useState } from 'react';

import Badge from 'src/components/badge/Badge';
import Button from 'src/components/button';
import Card from 'src/components/card';
import { BaseText } from 'src/components/typography';
import { PackageDescriptionRow } from 'src/components/pricing-package';
import { EUserType } from 'src/variables/enum-variables';
import './SignUpAsModal.scss';

interface ISignUpAsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectUserType: (userType: EUserType) => void;
}

const SignUpAsModal = (props: ISignUpAsModalProps): JSX.Element => {
  const { isOpen, onClose, onSelectUserType } = props;
  const [selectedCard, setSelectedCard] = useState<EUserType | null>(null);

  const roleCards = [
    {
      type: EUserType.SOLO_PRACTITIONER,
      title: 'Solo Practitioner',
      subtitle: 'I see clients and manage my own practice',
      icon: <UserOutlined className="role-card__icon" />,
      description: 'Choose this if you see and treat clients directly as an independent practitioner.',
      features: [
        'See and treat clients directly',
        'Access all client records and session notes',
        'Create and assign homework and tasks',
      ],
      cannotDo: [
        'Manage other practitioners or staff',
        'Upgrade to a Clinic Owner account'
      ],
      buttonText: 'Continue as Solo Practitioner →',
      badgeText: 'Direct client care',
      badgeColor: 'violet' as const,
      isPrimary: true,
      warningText: null
    },
    {
      type: EUserType.CLINIC_OWNER,
      title: 'Clinic Owner/Administrator',
      subtitle: 'I manage practitioners but don\'t see clients myself',
      icon: <TeamOutlined className="role-card__icon" />,
      description: 'Choose this ONLY if you manage practitioners.',
      features: [
        'Manage multiple practitioners on your team',
        'Oversee clinic-wide operations and reporting',
        'Handle administrative controls and billing',
        'Invite and manage practitioner accounts'
      ],
      cannotDo: [
        'See or treat clients directly',
        'Access individual client records or sessions',
        'Create session notes or treatment plans',
        'Downgrade to a Solo Practitioner account'
      ],
      buttonText: 'Continue as Clinic Owner →',
      badgeText: 'Management only',
      badgeColor: 'green' as const,
      isPrimary: false,
      warningText: 'Need to see clients AND manage practitioners? You\'ll need two separate accounts with different email addresses.'
    }
  ];

  const handleCardClick = (cardType: EUserType) => {
    setSelectedCard(selectedCard === cardType ? null : cardType);
  };

  const handleSelectUserType = (userType: EUserType) => {
    // Add a small delay to let the user see the selection before transitioning
    setTimeout(() => {
      onSelectUserType(userType);
    }, 300);
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      centered
      closable={false}
      footer={null}
      width={900}
      className="SignUpAsModal"
    >
      <Space direction="vertical" className="sign-up-as-modal" size={0}>
        <div className="sign-up-as-modal__header">
          <BaseText type="display1" textAlign="center">Choose Your Role</BaseText>
        </div>

        <Alert
          message="This choice is permanent - you cannot change roles or email later"
          type="warning"
          showIcon
          className="sign-up-as-modal__alert"
        />
        
        <Row gutter={[24, 24]} className="sign-up-as-modal__cards">
          {roleCards.map((card) => (
            <Col xs={24} md={12} key={card.type}>
              <Card 
                className={`role-card ${card.isPrimary ? 'role-card--primary' : ''} ${selectedCard === card.type ? 'role-card--selected' : ''}`}
                onClick={() => handleCardClick(card.type)}
              >
                <div className="role-card__header">
                  <div className="role-card__icon-container">
                    {selectedCard === card.type ? (
                      <CheckOutlined className="role-card__icon role-card__icon--selected" />
                    ) : (
                      card.icon
                    )}
                  </div>
                  <BaseText type="subHeading" className="role-card__title">
                    {card.title}
                  </BaseText>
                  {selectedCard === card.type && (
                    <div className="role-card__selected-indicator">
                      <CheckOutlined />
                    </div>
                  )}
                </div>
                
                <BaseText type="small" className="role-card__subtitle">
                  {card.subtitle}
                </BaseText>
                
                <Divider className="role-card__divider" />
                
                <Badge color={card.badgeColor} text={card.badgeText} />
                
                <div className="role-card__description">
                  <BaseText type="body1" className="role-card__description-text">
                    {card.description}
                  </BaseText>


                  
                  <div className="role-card__features">
                    <BaseText type="caption" className="role-card__features-title">
                      What you CAN do:
                    </BaseText>
                    {card.features.map((feature, index) => (
                      <PackageDescriptionRow key={index} text={feature} />
                    ))}
                  </div>

                  <div className="role-card__limitations-section">
                    <BaseText type="caption" className="role-card__limitations-title">
                      What you CANNOT do:
                    </BaseText>
                    {card.cannotDo.map((limitation, index) => (
                      <div key={index} className="role-card__limitation-item">
                        <CloseOutlined className="role-card__limitation-icon" />
                        <BaseText type="caption" className="role-card__limitation-text">{limitation}</BaseText>
                      </div>
                    ))}
                  </div>

                  {card.warningText && (
                    <div className="role-card__warning" style={{ marginBottom: '12px' }}>
                      <ExclamationCircleOutlined className="role-card__warning-icon" />
                      <BaseText type="caption" className="role-card__warning-text">
                        {card.warningText}
                      </BaseText>
                    </div>
                  )}
                </div>

                {selectedCard === card.type && (
                  <Button
                    className="role-card__button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectUserType(card.type);
                    }}
                    type="primary"
                  >
                    <BaseText inline={true}>{card.buttonText}</BaseText>
                  </Button>
                )}
              </Card>
            </Col>
          ))}
        </Row>

        <div className="sign-up-as-modal__help-section">
          <BaseText type="body2" textAlign="center" className="sign-up-as-modal__help-text">
            <strong>See clients yourself?</strong> → Solo Practitioner • <strong>Only manage others?</strong> → Clinic Owner • <strong>Need both?</strong> → Two separate accounts with different emails
          </BaseText>
        </div>
      </Space>
    </Modal>
  );
};

export default SignUpAsModal;
